import { linkify, safeHref, shortUrl } from "@nmt/shared";
import type { ReactNode } from "react";
import { cx } from "./ui";

/** Tags kept from HTML in notes (from JobNimbus, Zapier, QuickBooks); anything else shows as its text. */
const KEEP: Record<string, string> = { b: "strong", strong: "strong", i: "em", em: "em", u: "u", ul: "ul", ol: "ol", li: "li" };
const BLOCK = new Set(["p", "div", "br", "li", "tr", "h1", "h2", "h3", "h4"]);
const looksLikeHtml = (s: string) => /<\/?(a|b|strong|i|em|u|p|div|br|span|ul|ol|li|font|table|tr|td)\b[^>]*>/i.test(s);

function Link({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={href} className="font-medium text-brand underline decoration-brand/40 hover:decoration-brand">
      {children ?? shortUrl(href)}
    </a>
  );
}

/** Text with its web addresses as short links. */
function Linked({ text }: { text: string }) {
  return (
    <>
      {linkify(text).map((p, i) => (p.kind === "text" ? p.text : <Link key={i} href={p.href}>{p.label}</Link>))}
    </>
  );
}

/**
 * HTML turned into React elements from an allow-list (never inserted as HTML): links (web, email,
 * phone only, opening in a new tab), bold, italics, underline, lists, and line breaks. Scripts,
 * styles, images, and every attribute but a link's address are dropped.
 */
function fromHtml(html: string): ReactNode[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let key = 0;
  const walk = (node: Node): ReactNode[] => {
    if (node.nodeType === Node.TEXT_NODE) return [<Linked key={key++} text={node.textContent ?? ""} />];
    if (node.nodeType !== Node.ELEMENT_NODE) return [];
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (["script", "style", "head", "title", "iframe", "object", "embed", "template"].includes(tag)) return [];
    if (tag === "br") return ["\n"];
    const kids = [...el.childNodes].flatMap(walk);
    if (tag === "a") {
      const href = safeHref(el.getAttribute("href") ?? "");
      const label = (el.textContent ?? "").trim();
      if (!href) return kids;
      return [<Link key={key++} href={href}>{label && label !== href ? label : undefined}</Link>];
    }
    const out: ReactNode[] = KEEP[tag] ? [createKept(KEEP[tag]!, key++, kids)] : kids;
    return BLOCK.has(tag) && tag !== "li" ? [...out, "\n"] : out;
  };
  return [...doc.body.childNodes].flatMap(walk);
}

function createKept(tag: string, key: number, kids: ReactNode[]) {
  if (tag === "strong") return <strong key={key}>{kids}</strong>;
  if (tag === "em") return <em key={key}>{kids}</em>;
  if (tag === "u") return <u key={key}>{kids}</u>;
  if (tag === "ul") return <ul key={key} className="list-disc pl-5 whitespace-normal">{kids}</ul>;
  if (tag === "ol") return <ol key={key} className="list-decimal pl-5 whitespace-normal">{kids}</ol>;
  return <li key={key}>{kids}</li>;
}

/** A note or message body: wraps (even long words and addresses), links open in a new tab, HTML shown safely. */
export function RichText({ text, className }: { text: string; className?: string }) {
  return <div className={cx("whitespace-pre-wrap [overflow-wrap:anywhere]", className)}>{looksLikeHtml(text) ? fromHtml(text) : <Linked text={text} />}</div>;
}
